import React, { useMemo, useState } from 'react';
import { ArrowRightLeft, CheckCircle2, Clock3, XCircle } from 'lucide-react';
import { eventsService } from '../../../../../../services';
import './OwnershipTransferInbox.css';

const formatRequestedAt = (value) => {
    if (!value) {
        return 'just now';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return 'recently';
    }

    return parsed.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const OwnershipTransferInbox = ({ transfers, onTransfersChanged }) => {
    const [busyTransferId, setBusyTransferId] = useState(null);
    const [error, setError] = useState(null);

    const incomingTransfers = useMemo(
        () => (Array.isArray(transfers) ? transfers.filter((transfer) => transfer.status === 'Pending') : []),
        [transfers]
    );

    const runTransferAction = async (transferId, action) => {
        setBusyTransferId(transferId);
        setError(null);

        try {
            if (action === 'accept') {
                await eventsService.acceptOwnershipTransfer(transferId);
            } else {
                await eventsService.declineOwnershipTransfer(transferId);
            }

            if (onTransfersChanged) {
                await onTransfersChanged();
            }
        } catch (actionError) {
            console.error(`Error trying to ${action} ownership transfer:`, actionError);
            setError(actionError?.message || `Could not ${action} that transfer request.`);
        } finally {
            setBusyTransferId(null);
        }
    };

    if (incomingTransfers.length === 0 && !error) {
        return null;
    }

    return (
        <section className="ownership-transfer-inbox">
            <div className="ownership-transfer-inbox-header">
                <div className="ownership-transfer-inbox-icon">
                    <ArrowRightLeft size={22} />
                </div>
                <div>
                    <h2>Ownership Requests</h2>
                    <p>Accept to take over an event, or decline if it should stay where it is.</p>
                </div>
            </div>

            {error ? <div className="score-events-toast toast-error"><span>{error}</span></div> : null}

            <div className="ownership-transfer-grid">
                {incomingTransfers.map((transfer) => (
                    <article key={transfer.eventOwnershipTransferId} className="ownership-transfer-card">
                        <div className="ownership-transfer-card-top">
                            <div>
                                <div className="ownership-transfer-badge">
                                    <Clock3 size={14} />
                                    Pending
                                </div>
                                <h3>{transfer.eventName}</h3>
                                <p>{transfer.clubName || 'Venue pending'}</p>
                            </div>
                            <span className="ownership-transfer-type">{transfer.eventType}</span>
                        </div>

                        <div className="ownership-transfer-meta">
                            <span>From: {transfer.fromUserName}</span>
                            <span>Requested: {formatRequestedAt(transfer.requestedAt)}</span>
                        </div>

                        <div className="ownership-transfer-actions">
                            <button
                                type="button"
                                className="dashboard-default-btn"
                                disabled={busyTransferId === transfer.eventOwnershipTransferId}
                                onClick={() => runTransferAction(transfer.eventOwnershipTransferId, 'accept')}
                            >
                                <CheckCircle2 size={16} />
                                {busyTransferId === transfer.eventOwnershipTransferId ? 'Working...' : 'Accept Ownership'}
                            </button>
                            <button
                                type="button"
                                className="dashboard-card-outline-btn"
                                disabled={busyTransferId === transfer.eventOwnershipTransferId}
                                onClick={() => runTransferAction(transfer.eventOwnershipTransferId, 'decline')}
                            >
                                <XCircle size={16} />
                                Decline
                            </button>
                        </div>
                    </article>
                ))}
            </div>
        </section>
    );
};

export default OwnershipTransferInbox;
